import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import SummaryApi from "../common";
import displayINRCurrency from "../helpers/displayCurrency";
import DataTable from "../components/DataTable";
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCity,
  FaShoppingCart,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaCrown,
  FaBoxOpen,
  FaTruck,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEdit,
  FaCamera,
  FaRupeeSign,
  FaBirthdayCake,
  FaVenusMars,
  FaEye,
  FaEyeSlash,
  FaShareAlt,
  FaClipboardCheck 
} from "react-icons/fa";
import { motion } from "framer-motion";
import { CheckCircle, Repeat } from "lucide-react";

// ============================================================================
// STATUS BADGE COMPONENT
// ============================================================================
const StatusBadge = ({ status }) => {
  const statusMap = {
    Active: {
      color: "bg-green-100 text-green-800 border-green-200",
      icon: <FaCheckCircle className="inline mr-1" />,
    },
    Expired: {
      color: "bg-red-100 text-red-800 border-red-200",
      icon: <FaTimesCircle className="inline mr-1" />,
    },
    Pending: {
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      icon: <FaClock className="inline mr-1" />,
    },
  };
  const config = statusMap[status] || {
    color: "bg-gray-100 text-gray-800 border-gray-200",
    icon: <FaClock className="inline mr-1" />,
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${config.color}`}
    >
      {config.icon}
      {status}
    </span>
  );
};

// ============================================================================
// CUSTOM SUCCESS TOAST
// ============================================================================
const showSuccessToast = (title, description) => {
  if (!title || !description) return;

  toast.dismiss("profile-success");

  toast.custom(
    () => (
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.5 }}
        className="pointer-events-auto max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] flex border-b-4 border-rose-500"
      >
        <div className="flex-1 p-4">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-500">
              <CheckCircle size={28} />
            </div>

            <div>
              <p className="text-sm font-bold text-rose-950">{title}</p>
              <p className="text-xs text-rose-900/60 mt-1">{description}</p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => toast.dismiss("profile-success")}
          className="px-4 font-bold text-rose-600"
        >
          Close
        </button>
      </motion.div>
    ),
    {
      id: "profile-success",
      duration: 3000,
    },
  );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================
const PersonalDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backendDomain = SummaryApi.userDetailsById.url
    .split("/")
    .slice(0, 3)
    .join("/");

  const [userDetails, setUserDetails] = useState(null);
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [walletPoints, setWalletPoints] = useState(0);
  const [walletLoading, setWalletLoading] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [transactionSearch, setTransactionSearch] = useState("");
  const [subscriptionSearch, setSubscriptionSearch] = useState("");
  // Add near other states
  const [subscriptionOrders, setSubscriptionOrders] = useState([]);
  const [subscriptionOrdersLoading, setSubscriptionOrdersLoading] =
    useState(false);
  const [subscriptionOrderSearch, setSubscriptionOrderSearch] = useState("");
  const [editFormData, setEditFormData] = useState({
    user_name: "",
    mobile_number: "",
    email: "",
    city: "",
    address: "",
  });
  const [profilePreview, setProfilePreview] = useState(null);
  const [selectedProfileImage, setSelectedProfileImage] = useState(null);
  const [updating, setUpdating] = useState(false);

  const [orderPage, setOrderPage] = useState(1);
  const [subPage, setSubPage] = useState(1);
  const ordersPerPage = 5;
  const subsPerPage = 5;
  // Add these states
  const [refTransactions, setRefTransactions] = useState([]);
  const [refTransactionsLoading, setRefTransactionsLoading] = useState(false);
  const [refTransactionSearch, setRefTransactionSearch] = useState("");
  // Password visibility toggle
  const [showPassword, setShowPassword] = useState(false);
const [counts, setCounts] = useState({
    total_questions_attempted: 0,
    total_active_packages: 0,
    total_used_packages: 0,
    total_orders: 0,
    today_orders: 0
  });
// Around Line 175 in your file
const fetchDashboardCounts = async () => {
  if (!userDetails?.user_id) return; // Guard clause
  
  try {
    const response = await fetch(SummaryApi.user_dashboard_counts.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      // Change user_id to userDetails.user_id
      body: JSON.stringify({ user_id: userDetails.user_id }) 
    });

    const dataResponse = await response.json();

    if (dataResponse.status) {
      setCounts(dataResponse.data);
    }
  } catch (error) {
    console.error("Error fetching dashboard counts:", error);
  }
};
  useEffect(() => {
    if (orderModalOpen || editModalOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "auto";
      document.body.style.position = "static";
    }

    return () => {
      document.body.style.overflow = "auto";
      document.body.style.position = "static";
    };
  }, [orderModalOpen, editModalOpen]);

  useEffect(() => {
    const validateAndFetchUser = async () => {
      let userId = null;
      try {
        const decoded = atob(id);
        const num = parseInt(decoded, 10);
        if (isNaN(num)) throw new Error("Invalid ID");
        userId = num;
      } catch (err) {
        setInvalidLink(true);
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(SummaryApi.userDetailsById.url, {
          method: SummaryApi.userDetailsById.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const result = await response.json();
        if (result.status && result.data) {
          setUserDetails(result.data);
          setEditFormData({
            user_name: result.data.user_name || "",
            mobile_number: result.data.mobile_number || "",
            email: result.data.email || "",
            city: result.data.city || "",
            address: result.data.address || "",
          });
          if (result.data.profile_image) {
            setProfilePreview(`${backendDomain}/${result.data.profile_image}`);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    validateAndFetchUser();
  }, [id, backendDomain]);

  const fetchSubscriptionOrders = async () => {
    if (!userDetails?.user_id) return;

    setSubscriptionOrdersLoading(true);
    try {
      const response = await fetch(SummaryApi.subscription_order_by_user.url, {
        method: SummaryApi.subscription_order_by_user.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userDetails.user_id }),
      });

      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        // Optionally enrich with plan & package names (like in MyOrders)
        const enriched = await Promise.all(
          result.data.map(async (order) => {
            let planName = "—";
            let packageName = "—";

            if (order.plan_id) {
              try {
                const planRes = await fetch(
                  SummaryApi.plan_admin_retrieve_by_id.url,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ plan_id: order.plan_id }),
                  },
                );
                const planJson = await planRes.json();
                if (planJson.status && planJson.data?.plan_name) {
                  planName = planJson.data.plan_name;
                }
              } catch (e) {}
            }

            if (order.package_id) {
              try {
                const pkgRes = await fetch(
                  SummaryApi.package_retrieve_by_id.url,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ package_id: order.package_id }),
                  },
                );
                const pkgJson = await pkgRes.json();
                if (pkgJson.status && pkgJson.data?.package_name) {
                  packageName = pkgJson.data.package_name;
                }
              } catch (e) {}
            }

            return {
              ...order,
              plan_name: planName,
              package_name: packageName,
            };
          }),
        );

        setSubscriptionOrders(enriched);
      } else {
        setSubscriptionOrders([]);
      }
    } catch (error) {
      console.error("Failed to fetch subscription orders:", error);
      setSubscriptionOrders([]);
    } finally {
      setSubscriptionOrdersLoading(false);
    }
  };
  useEffect(() => {
    if (userDetails) {
      fetchOrders();
      fetchTransactions();
      fetchSubscriptions(); // already there (active subs)
      fetchReferralTransactions();
      fetchWalletBalance();
      fetchSubscriptionOrders(); // ← new
         fetchDashboardCounts();
    }
  }, [userDetails]);

  const filteredSubscriptionOrders = subscriptionOrders.filter(
    (order) =>
      (order.plan_name?.toLowerCase() || "").includes(
        subscriptionOrderSearch.toLowerCase(),
      ) ||
      (order.package_name?.toLowerCase() || "").includes(
        subscriptionOrderSearch.toLowerCase(),
      ) ||
      (order.pads_count?.toString() || "").includes(subscriptionOrderSearch) ||
      (order.amount?.toString() || "").includes(subscriptionOrderSearch) ||
      (order.order_code?.toLowerCase() || "").includes(
        subscriptionOrderSearch.toLowerCase(),
      ),
  );
  const fetchReferralTransactions = async () => {
    if (!userDetails?.user_id) return;

    setRefTransactionsLoading(true);
    try {
      const response = await fetch(
        SummaryApi.wallet_ref_transaction_by_referral.url,
        {
          method: SummaryApi.wallet_ref_transaction_by_referral.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ referral_id: userDetails.user_id }),
        },
      );

      const result = await response.json();

      if (result.status && Array.isArray(result.data)) {
        // Only keep transactions that actually gave points to someone
        const meaningful = result.data.filter(
          (item) =>
            Number(item.referral_points || 0) > 0 ||
            Number(item.referee_points || 0) > 0,
        );
        setRefTransactions(meaningful);
      } else {
        setRefTransactions([]);
      }
    } catch (error) {
      console.error("Failed to fetch referral transactions:", error);
    } finally {
      setRefTransactionsLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    if (!userDetails?.user_id) return;

    setWalletLoading(true);
    try {
      const response = await fetch(SummaryApi.wallet_points.url, {
        method: SummaryApi.wallet_points.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userDetails.user_id }), // or user_id — test what works
      });

      const result = await response.json();

      let points = 0;
      if (result.status) {
        // Case 1: backend returns direct balance
        if (result.data?.wallet_points !== undefined) {
          points = Number(result.data.wallet_points) || 0;
        }
        // Case 2: backend returns array of transactions → sum referral_points
        else if (Array.isArray(result.data)) {
          points = result.data.reduce(
            (sum, tx) => sum + (Number(tx.referral_points) || 0),
            0,
          );
        }
        // Case 3: other possible direct fields
        else if (result.data?.points || result.data?.balance) {
          points = Number(result.data.points || result.data.balance || 0);
        }
      }

      setWalletPoints(points);
    } catch (error) {
      console.error("Failed to fetch wallet balance:", error);
      setWalletPoints(0);
    } finally {
      setWalletLoading(false);
    }
  };

  const fetchOrders = async () => {
    if (!userDetails?.user_id) return;
    setOrdersLoading(true);
    try {
      const response = await fetch(SummaryApi.OrderByCustomers.url, {
        method: SummaryApi.OrderByCustomers.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userDetails.user_id }),
      });
      const result = await response.json();
      if (result.status && result.data) setOrders(result.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchTransactions = async () => {
    if (!userDetails?.user_id) return;
    setTransactionsLoading(true);
    try {
      const response = await fetch(SummaryApi.transactionsbyuser.url, {
        method: SummaryApi.transactionsbyuser.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userDetails.user_id }),
      });
      const result = await response.json();
      if (result.status && result.data) setTransactions(result.data);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    if (!userDetails?.user_id) return;
    setSubscriptionsLoading(true);
    try {
      const response = await fetch(SummaryApi.subscriptionbyuseradmin.url, {
        method: SummaryApi.subscriptionbyuseradmin.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userDetails.user_id }),
      });
      const result = await response.json();
      if (result.status && result.data) setSubscriptions(result.data);
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
    } finally {
      setSubscriptionsLoading(false);
    }
  };
  useEffect(() => {
    if (userDetails) {
      fetchOrders();
      fetchTransactions();
      fetchSubscriptions();
      fetchReferralTransactions();
      fetchWalletBalance(); // ← added here
    }
  }, [userDetails]);

  const filteredRefTransactions = refTransactions.filter((tx) => {
    const search = refTransactionSearch.toLowerCase();
    return (
      (tx.referee_name?.toLowerCase() || "").includes(search) ||
      (tx.referee_mobile_number?.toLowerCase() || "").includes(search) ||
      (tx.referral_points?.toString() || "").includes(search) ||
      (tx.referee_points?.toString() || "").includes(search) ||
      (tx.created_date?.toLowerCase() || "").includes(search) ||
      (tx.transaction_date?.toLowerCase() || "").includes(search)
    );
  });
  const filteredOrders = orders.filter(
    (order) =>
      (order.order_code?.toLowerCase() || "").includes(
        orderSearch.toLowerCase(),
      ) ||
      (order.order_status?.toLowerCase() || "").includes(
        orderSearch.toLowerCase(),
      ) ||
      (order.total_amount?.toString() || "").includes(orderSearch),
  );
  const currentOrders = filteredOrders.slice(
    (orderPage - 1) * ordersPerPage,
    orderPage * ordersPerPage,
  );

  const filteredTransactions = transactions.filter(
    (tx) =>
      (tx.transaction_type?.toLowerCase() || "").includes(
        transactionSearch.toLowerCase(),
      ) ||
      (tx.amount?.toString() || "").includes(transactionSearch) ||
      (tx.status?.toLowerCase() || "").includes(
        transactionSearch.toLowerCase(),
      ),
  );

  const filteredSubscriptions = subscriptions.filter(
    (sub) =>
      (sub.pads_count?.toString() || "").includes(subscriptionSearch) ||
      (sub.price?.toString() || "").includes(subscriptionSearch) ||
      (sub.payment_mode?.toLowerCase() || "").includes(
        subscriptionSearch.toLowerCase(),
      ),
  );

  const currentSubscriptions = filteredSubscriptions.slice(
    (subPage - 1) * subsPerPage,
    subPage * subsPerPage,
  );

  const totalOrdersCount = orders.length;
  const totalSpent = orders.reduce(
    (sum, o) => sum + parseFloat(o.total_amount || 0),
    0,
  );
  const todayOrders = orders.filter(
    (o) =>
      new Date(o.order_date_time).toDateString() === new Date().toDateString(),
  ).length;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // UPDATED: Now perfectly handles null, undefined, empty string, and any missing value
  const displayValue = (value) => {
    if (
      value === "" ||
      value === null ||
      value === undefined ||
      value === "null" ||
      value === "undefined"
    )
      return "N/A";
    return value;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-100">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-t-transparent animate-spin"></div>
          <p className="mt-6 text-lg text-purple-700">Loading...</p>
        </div>
      </div>
    );
  }

  if (invalidLink || !userDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center p-8 bg-white rounded-2xl shadow-xl max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-3">
            {invalidLink ? "Invalid Link" : "User Not Found"}
          </h2>
          <p className="text-gray-600">
            {invalidLink
              ? "This dashboard link is invalid or expired."
              : "We couldn't find your account."}
          </p>
        </div>
      </div>
    );
  }

  const profileImageUrl =
    profilePreview ||
    (userDetails.profile_image
      ? `${backendDomain}/${userDetails.profile_image}`
      : null);
  

  
  return (
    <>
      <Toaster position="top-center" toastOptions={{ duration: 3500 }} />
      <div className="min-h-screen bg-gradient-to-br to-indigo-100 pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-center mb-10 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
            My Dashboard
          </h1>

          {/* Stats Widgets */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
        
        {/* Total Orders */}
        <div className="bg-white rounded-2xl shadow-md border border-pink-100 p-6 flex items-center gap-5 hover:shadow-xl transition-all duration-300">
          <div className="bg-pink-50 rounded-full p-4">
            <FaShoppingCart className="text-pink-600 text-4xl" />
          </div>
          <div>
            <p className="text-gray-500 text-sm font-medium">Total Orders</p>
            <p className="text-3xl font-bold text-pink-800">{counts.total_orders}</p>
          </div>
        </div>

        {/* Today's Orders */}
        <div className="bg-white rounded-2xl shadow-md border border-orange-100 p-6 flex items-center gap-5 hover:shadow-xl transition-all duration-300">
          <div className="bg-orange-50 rounded-full p-4">
            <FaCalendarAlt className="text-orange-600 text-4xl" />
          </div>
          <div>
            <p className="text-gray-500 text-sm font-medium">Today's Orders</p>
            <p className="text-3xl font-bold text-orange-800">{counts.today_orders}</p>
          </div>
        </div>

        {/* Total Spent - (Note: If API doesn't provide this, you may need a separate field or keep static) */}
        <div className="bg-white rounded-2xl shadow-md border border-teal-100 p-6 flex items-center gap-5 hover:shadow-xl transition-all duration-300">
          <div className="bg-teal-50 rounded-full p-4">
            <FaMoneyBillWave className="text-teal-600 text-4xl" />
          </div>
          <div>
            <p className="text-gray-500 text-sm font-medium">Used Packages</p>
            <p className="text-3xl font-bold text-teal-800">{counts.total_used_packages}</p>
          </div>
        </div>

        {/* Active Plans */}
        <div className="bg-white rounded-2xl shadow-md border border-emerald-100 p-6 flex items-center gap-5 hover:shadow-xl transition-all duration-300">
          <div className="bg-emerald-50 rounded-full p-4">
            <FaCrown className="text-emerald-600 text-4xl" />
          </div>
          <div>
            <p className="text-gray-500 text-sm font-medium">Active Plans</p>
            <p className="text-3xl font-bold text-emerald-800">{counts.total_active_packages}</p>
          </div>
        </div>

        {/* Questions Attempted */}
        <div className="bg-white rounded-2xl shadow-md border border-blue-100 p-6 flex items-center gap-5 hover:shadow-xl transition-all duration-300">
          <div className="bg-blue-50 rounded-full p-4">
            <FaClipboardCheck className="text-blue-600 text-4xl" />
          </div>
          <div>
            <p className="text-gray-500 text-sm font-medium">Questions Attempted</p>
            <p className="text-3xl font-bold text-blue-800">{counts.total_questions_attempted}</p>
          </div>
        </div>

      </div>

          {/* Profile + Transaction History */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Profile Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-6 relative">
                <button
                  onClick={() => setEditModalOpen(true)}
                  className="absolute top-4 right-4 bg-white/25 hover:bg-white/40 p-2.5 rounded-full transition"
                >
                  <FaEdit size={18} />
                </button>
                <div className="flex items-center gap-5">
                  <div className="relative w-28 h-28 rounded-full border-4 border-white/50 overflow-hidden shadow-xl">
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-white/20 flex items-center justify-center">
                        <FaUser size={56} className="text-white/70" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1">
                      <h2 className="text-2xl font-bold text-white">
                        {displayValue(userDetails.user_name)}
                      </h2>

                      {/* Wallet display – now in the same row/line */}
                      <div className="inline-flex items-center gap-2 bg-white/25 backdrop-blur-sm px-3.5 py-1.5 rounded-xl border border-white/30 shadow-sm">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="text-yellow-300 drop-shadow-md"
                        >
                          <path
                            d="M21 12V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V12Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <circle cx="13" cy="12" r="3" fill="currentColor" />
                          <circle
                            cx="7"
                            cy="12"
                            r="2"
                            fill="currentColor"
                            opacity="0.7"
                          />
                          <path
                            d="M3 9H21"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>

                        <span className="text-white font-bold text-base tracking-tight">
                          {walletLoading ? "•••" : walletPoints}
                          <span className="text-sm font-medium ml-1 opacity-90">
                            pts
                          </span>
                        </span>
                      </div>
                    </div>

                    <p className="text-white/80 text-sm mt-1">Welcome back!</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaPhoneAlt className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex">
                        <p className="w-32 text-sm font-semibold text-gray-700">
                          Mobile No
                        </p>
                        <p className="px-1 text-sm font-semibold text-gray-700">
                          :
                        </p>
                        <p className="text-sm font-normal text-gray-800">
                          {displayValue(userDetails.mobile_number)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaPhoneAlt className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex">
                        <p className="w-32 text-sm font-semibold text-gray-700">
                          Alt Mobile No
                        </p>
                        <p className="px-1 text-sm font-semibold text-gray-700">
                          :
                        </p>
                        <p className="text-sm font-normal text-gray-800">
                          {displayValue(userDetails.alternate_mobile_number)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaEnvelope className="text-red-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex">
                        <p className="w-32 text-sm font-semibold text-gray-700">
                          Email
                        </p>
                        <p className="px-1 text-sm font-semibold text-gray-700">
                          :
                        </p>
                        <p className="text-sm font-normal text-gray-800 truncate">
                          {displayValue(userDetails.email)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaVenusMars className="text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex">
                        <p className="w-32 text-sm font-semibold text-gray-700">
                          Gender
                        </p>
                        <p className="px-1 text-sm font-semibold text-gray-700">
                          :
                        </p>
                        <p className="text-sm font-normal text-gray-800">
                          {displayValue(userDetails.gender)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaBirthdayCake className="text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex">
                        <p className="w-32 text-sm font-semibold text-gray-700">
                          Date of Birth
                        </p>
                        <p className="px-1 text-sm font-semibold text-gray-700">
                          :
                        </p>
                        <p className="text-sm font-normal text-gray-800">
                          {formatDate(userDetails.date_of_birth)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaCity className="text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex">
                        <p className="w-32 text-sm font-semibold text-gray-700">
                          City
                        </p>
                        <p className="px-1 text-sm font-semibold text-gray-700">
                          :
                        </p>
                        <p className="text-sm font-normal text-gray-800">
                          {displayValue(userDetails.city)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaRupeeSign className="text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex">
                        <p className="w-32 text-sm font-semibold text-gray-700">
                          Coupon Code
                        </p>
                        <p className="px-1 text-sm font-semibold text-gray-700">
                          :
                        </p>
                        <p className="text-sm font-normal text-gray-800">
                          {displayValue(userDetails.coupon_code)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaShareAlt className="text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex">
                        <p className="w-32 text-sm font-semibold text-gray-700">
                          Refferal Code
                        </p>
                        <p className="px-1 text-sm font-semibold text-gray-700">
                          :
                        </p>
                        <p className="text-sm font-normal text-gray-800">
                          {displayValue(userDetails.user_code)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaCalendarAlt className="text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex">
                        <p className="w-32 text-sm font-semibold text-gray-700">
                          Account Created
                        </p>
                        <p className="px-1 text-sm font-semibold text-gray-700">
                          :
                        </p>
                        <p className="text-sm font-normal text-gray-800">
                          {formatDate(userDetails.created_date)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaUser className="text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center">
                        <p className="w-32 text-sm font-semibold text-gray-700">
                          Password
                        </p>
                        <p className="px-1 text-sm font-semibold text-gray-700">
                          :
                        </p>
                        <p className="text-sm font-normal text-gray-800">
                          {showPassword
                            ? userDetails.password || "N/A"
                            : userDetails.password
                              ? "••••••••"
                              : "N/A"}
                        </p>
                        {userDetails.password && (
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="ml-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FaMapMarkerAlt className="text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-700 mb-1.5">
                      Delivery Address
                    </p>
                    <div className="bg-gray-50 p-3.5 rounded-xl text-gray-700 text-sm whitespace-pre-line">
                      {displayValue(userDetails.address)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction History + Recent Orders */}
            <div className="space-y-6">
              <DataTable
                title="Transaction History"
                subtitle="Vending machine pad transactions"
                searchPlaceholder="Search transactions..."
                searchValue={transactionSearch}
                onSearchChange={setTransactionSearch}
                data={filteredTransactions.slice(0, 10)}
                columns={[
                  {
                    key: "sno",
                    label: "S.No",
                    align: "center",
                    render: (_, index) => index + 1,
                  },
                  {
                    key: "opening_balance",
                    label: "Opening Balance",
                    align: "right",
                    render: (item) => (
                      <span className="font-medium">
                        {displayINRCurrency(item.opening_balance)}
                      </span>
                    ),
                  },
                  {
                    key: "withdraw_pads_count",
                    label: "Withdraw Pads",
                    align: "center",
                    render: (item) => (
                      <span className="font-medium">
                        {item.withdraw_pads_count || 0}
                      </span>
                    ),
                  },
                  {
                    key: "closing_balance",
                    label: "Closing Balance",
                    align: "right",
                    render: (item) => (
                      <span className="font-medium">
                        {displayINRCurrency(item.closing_balance)}
                      </span>
                    ),
                  },
                  {
                    key: "transaction_date",
                    label: "Transaction Date",
                    render: (item) => formatDate(item.transaction_date),
                  },
                ]}
                emptyMessage="No transactions yet"
                emptyIcon={FaRupeeSign}
                loading={transactionsLoading}
              />

              <DataTable
                title="Recent Orders"
                subtitle="Your latest purchases"
                searchPlaceholder="Search orders..."
                searchValue={orderSearch}
                onSearchChange={setOrderSearch}
                data={currentOrders}
                columns={[
                  {
                    key: "sno",
                    label: "S.No",
                    align: "center",
                    render: (_, index) =>
                      (orderPage - 1) * ordersPerPage + index + 1,
                  },
                  {
                    key: "order_code",
                    label: "Order",
                    render: (item) => (
                      <span className="font-medium">#{item.order_code}</span>
                    ),
                  },
                  {
                    key: "order_date_time",
                    label: "Date",
                    render: (item) => (
                      <span className="text-gray-600">
                        {formatDate(item.order_date_time)}
                      </span>
                    ),
                  },
                  {
                    key: "order_status",
                    label: "Status",
                    align: "center",
                    render: (item) => (
                      <StatusBadge status={item.order_status} />
                    ),
                  },
                  {
                    key: "total_amount",
                    label: "Amount",
                    align: "right",
                    render: (item) => (
                      <span className="font-medium">
                        {displayINRCurrency(item.total_amount)}
                      </span>
                    ),
                  },
                  {
                    key: "action",
                    label: "",
                    align: "center",
                    render: () => (
                      <span className="text-indigo-600 hover:text-indigo-800 text-sm font-medium cursor-pointer">
                        View
                      </span>
                    ),
                  },
                ]}
                emptyMessage="No orders placed yet"
                emptyIcon={FaBoxOpen}
                loading={ordersLoading}
                rowClickHandler={(order) => {
                  setSelectedOrder({
                    ...order,
                    items: order.order_items || [],
                  });
                  setOrderModalOpen(true);
                }}
                pagination={
                  filteredOrders.length > 5
                    ? {
                        currentPage: orderPage,
                        totalPages: Math.ceil(
                          filteredOrders.length / ordersPerPage,
                        ),
                        onPrev: () =>
                          orderPage > 1 && setOrderPage(orderPage - 1),
                        onNext: () =>
                          orderPage <
                            Math.ceil(filteredOrders.length / ordersPerPage) &&
                          setOrderPage(orderPage + 1),
                        showingText: `${(orderPage - 1) * ordersPerPage + 1}–${Math.min(orderPage * ordersPerPage, filteredOrders.length)} of ${filteredOrders.length}`,
                      }
                    : null
                }
              />
            </div>
          </div>

          {/* Subscriptions Table */}
          <div className="mb-8">
            <DataTable
              title="Subscriptions"
              subtitle="Your subscription packages history"
              searchPlaceholder="Search by pads, price, mode..."
              searchValue={subscriptionSearch}
              onSearchChange={setSubscriptionSearch}
              data={currentSubscriptions}
              columns={[
                {
                  key: "sno",
                  label: "S.No",
                  align: "center",
                  render: (_, index) => (subPage - 1) * subsPerPage + index + 1,
                },
                {
                  key: "pads_count",
                  label: "Pads",
                  align: "center",
                  render: (item) => (
                    <span className="font-medium">{item.pads_count}</span>
                  ),
                },
                {
                  key: "price",
                  label: "Price",
                  align: "right",
                  render: (item) => (
                    <span className="font-medium">
                      {displayINRCurrency(item.price)}
                    </span>
                  ),
                },
                {
                  key: "discount",
                  label: "Disc %",
                  align: "center",
                  render: (item) => (item.discount ? `${item.discount}%` : "-"),
                },
                {
                  key: "period",
                  label: "Period",
                  render: (item) => (
                    <div className="text-sm whitespace-nowrap text-center font-medium">
                      {formatDate(item.package_start_date)} –{" "}
                      {formatDate(item.package_end_date)}
                    </div>
                  ),
                },
                {
                  key: "payment_mode",
                  label: "Payment Mode",
                  render: (item) => item.payment_mode || "Unknown",
                },
                {
                  key: "payment_status",
                  label: "Payment",
                  align: "center",
                  render: (item) => (
                    <span
                      className={
                        item.is_payment_success
                          ? "text-green-600 font-medium"
                          : "text-red-600 font-medium"
                      }
                    >
                      {item.is_payment_success ? "Success" : "Failed"}
                    </span>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  align: "center",
                  render: (item) => {
                    const endDate = new Date(item.package_end_date);
                    const now = new Date();
                    const statusText = item.is_active
                      ? "Active"
                      : endDate < now
                        ? "Expired"
                        : "Pending";
                    return <StatusBadge status={statusText} />;
                  },
                },
                {
                  key: "created",
                  label: "Subscribed On",
                  render: (item) => formatDate(item.created_date),
                },
              ]}
              emptyMessage="No subscriptions found"
              emptyIcon={FaCrown}
              loading={subscriptionsLoading}
              pagination={
                filteredSubscriptions.length > 5
                  ? {
                      currentPage: subPage,
                      totalPages: Math.ceil(
                        filteredSubscriptions.length / subsPerPage,
                      ),
                      onPrev: () => subPage > 1 && setSubPage(subPage - 1),
                      onNext: () =>
                        subPage <
                          Math.ceil(
                            filteredSubscriptions.length / subsPerPage,
                          ) && setSubPage(subPage + 1),
                      showingText: `${(subPage - 1) * subsPerPage + 1}–${Math.min(subPage * subsPerPage, filteredSubscriptions.length)} of ${filteredSubscriptions.length}`,
                    }
                  : null
              }
            />
          </div>
          <div className="mt-10 mb-8">
            <DataTable
              title="Referral Transactions"
              subtitle="Points earned from successful referrals"
              searchPlaceholder="Search by name, mobile, points..."
              searchValue={refTransactionSearch}
              onSearchChange={setRefTransactionSearch}
              data={filteredRefTransactions}
              columns={[
                {
                  key: "sno",
                  label: "S.No",
                  align: "center",
                  render: (_, index) => index + 1,
                },
                {
                  key: "referee",
                  label: "Referred Person",
                  render: (item) => (
                    <div className="text-sm">
                      <div className="font-medium">
                        {item.referee_name || "—"}
                      </div>
                      <div className="text-gray-500">
                        {item.referee_mobile_number || "—"}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "your_points",
                  label: "Your Points",
                  align: "center",
                  render: (item) => (
                    <span className="font-semibold text-green-700">
                      +{item.referral_points || 0}
                    </span>
                  ),
                },
                {
                  key: "friend_points",
                  label: "Referee's Points",
                  align: "center",
                  render: (item) => (
                    <span className="font-medium text-blue-700">
                      {item.referee_points || 0}
                    </span>
                  ),
                },
                {
                  key: "date",
                  label: "Date",
                  align: "center",
                  render: (item) => (
                    <span className="text-gray-700">
                      {formatDate(item.created_date || item.transaction_date)}
                    </span>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  align: "center",
                  render: (item) => (
                    <StatusBadge
                      status={item.is_active === 1 ? "Active" : "Inactive"}
                    />
                  ),
                },
              ]}
              emptyMessage="No referral activity yet"
              emptyIcon={FaShareAlt}
              loading={refTransactionsLoading}
            />
          </div>
          {/* ===================== SUBSCRIPTION ORDERS SECTION ===================== */}
          <div className="mt-10 mb-8">
            <DataTable
              title="Subscription Orders"
              subtitle="History of all your subscription purchases"
              searchPlaceholder="Search by plan, package, pads, amount, code..."
              searchValue={subscriptionOrderSearch}
              onSearchChange={setSubscriptionOrderSearch}
              data={filteredSubscriptionOrders}
              columns={[
                {
                  key: "sno",
                  label: "S.No",
                  align: "center",
                  render: (_, index) => index + 1,
                },
                {
                  key: "order_code",
                  label: "Order Code",
                  render: (item) =>
                    item.order_code ||
                    item.subscription_code ||
                    `SUB-${item.id || "—"}`,
                },
                {
                  key: "date",
                  label: "Date",
                  render: (item) =>
                    formatDate(
                      item.created_date ||
                        item.order_date_time ||
                        item.subscription_date ||
                        "—",
                    ),
                },
                {
                  key: "plan",
                  label: "Plan",
                  render: (item) => item.plan_name || "—",
                },
                {
                  key: "package",
                  label: "Package",
                  render: (item) => item.package_name || "—",
                },
                {
                  key: "pads",
                  label: "Pads/Cycle",
                  align: "center",
                  render: (item) => (
                    <span className="font-bold text-indigo-700">
                      {item.pads_count || item.pads_per_cycle || "?"}
                    </span>
                  ),
                },
                {
                  key: "amount",
                  label: "Amount",
                  align: "right",
                  render: (item) => (
                    <span className="font-bold text-indigo-700">
                      {displayINRCurrency(item.amount || item.price || 0)}
                    </span>
                  ),
                },
                {
                  key: "payment_mode",
                  label: "Payment Mode",
                  align: "center",
                  render: (item) => item.payment_mode || "Online",
                },
                {
                  key: "status",
                  label: "Status",
                  align: "center",
                  render: (item) => (
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        item.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-rose-100 text-rose-800"
                      }`}
                    >
                      {item.is_active ? "Active" : "Inactive"}
                    </span>
                  ),
                },
              ]}
              emptyMessage="No subscription orders found"
              emptyIcon={Repeat}
              loading={subscriptionOrdersLoading}
            />
          </div>
        </div>

        {/* ==================== ORDER DETAILS MODAL ==================== */}
        {orderModalOpen && selectedOrder && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2"
            onClick={() => setOrderModalOpen(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[75vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
                <div>
                  <h2 className="text-lg font-bold">
                    Order #{selectedOrder.order_code}
                  </h2>
                  <p className="text-white/80 text-xs mt-0.5">
                    {formatDate(selectedOrder.order_date_time)} •{" "}
                    {selectedOrder.payment_mode}
                  </p>
                </div>
                <button
                  onClick={() => setOrderModalOpen(false)}
                  className="p-1.5 rounded-full hover:bg-white/20 transition text-lg"
                >
                  ✕
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">Status</p>
                    <div className="mt-0.5">
                      <StatusBadge status={selectedOrder.order_status} />
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-base font-bold mt-0.5">
                      {displayINRCurrency(selectedOrder.total_amount)}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">Items</p>
                    <p className="text-base font-bold mt-0.5">
                      {selectedOrder.items?.length || 0}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-base font-semibold mb-1.5">
                    Delivery Address
                  </h3>
                  <div className="bg-gray-50 p-3 rounded-lg text-gray-700 text-sm whitespace-pre-line leading-relaxed">
                    {selectedOrder.order_address || "No address provided"}
                  </div>
                </div>
                <div>
                  <h3 className="text-base font-bold mb-2.5 flex items-center gap-2">
                    <FaBoxOpen className="text-purple-600" size={18} />
                    Items
                  </h3>
                  {selectedOrder.items?.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedOrder.items.map((item, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-sm transition-shadow flex flex-col h-full"
                        >
                          <div className="h-32 bg-gray-50 flex items-center justify-center p-2.5">
                            {item.product_img ? (
                              <img
                                src={`${backendDomain}/${item.product_img}`}
                                alt={item.product_name}
                                className="max-h-full object-contain"
                                onError={(e) =>
                                  (e.target.src =
                                    "https://via.placeholder.com/300x200?text=No+Image")
                                }
                              />
                            ) : (
                              <FaBoxOpen size={42} className="text-gray-300" />
                            )}
                          </div>
                          <div className="p-3 flex flex-col flex-1">
                            <h4 className="font-medium text-sm mb-2 line-clamp-2">
                              {item.product_name}
                            </h4>
                            <div className="space-y-1 text-xs mt-auto">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Price:</span>
                                <span>{displayINRCurrency(item.price)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Qty:</span>
                                <span>× {item.quantity}</span>
                              </div>
                              <div className="flex justify-between pt-1.5 border-t font-medium">
                                <span>Subtotal:</span>
                                <span>{displayINRCurrency(item.subtotal)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <FaBoxOpen
                        size={48}
                        className="mx-auto text-gray-300 mb-2"
                      />
                      <p className="text-gray-500 text-sm">No items</p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => setOrderModalOpen(false)}
                    className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow transition text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== EDIT PROFILE MODAL ==================== */}
        {editModalOpen && (
          <div
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => setEditModalOpen(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 py-2.5 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
                <h3 className="text-base font-bold">Edit Profile</h3>
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="p-1 hover:bg-white/20 rounded-full transition text-lg leading-none"
                >
                  ✕
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-200 shadow-sm">
                      {profilePreview ? (
                        <img
                          src={profilePreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <FaUser size={42} className="text-gray-400" />
                        </div>
                      )}
                    </div>
                    <label className="absolute -bottom-1 -right-1 bg-purple-600 text-white p-2 rounded-full cursor-pointer hover:bg-purple-700 shadow-md">
                      <FaCamera size={14} />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setSelectedProfileImage(file);
                            setProfilePreview(URL.createObjectURL(file));
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      name="user_name"
                      value={editFormData.user_name}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          user_name: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Mobile
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="mobile_number"
                          value={editFormData.mobile_number}
                          disabled
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Email
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={editFormData.email}
                          disabled
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={editFormData.city}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Address
                    </label>
                    <textarea
                      name="address"
                      value={editFormData.address}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          address: e.target.value,
                        }))
                      }
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition resize-none"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={async () => {
                      if (!userDetails?.user_id) return;
                      setUpdating(true);
                      try {
                        const formData = new FormData();
                        formData.append("user_id", userDetails.user_id);
                        formData.append("user_name", editFormData.user_name);
                        formData.append(
                          "mobile_number",
                          editFormData.mobile_number,
                        );
                        formData.append("email", editFormData.email);
                        formData.append("city", editFormData.city);
                        formData.append("address", editFormData.address);
                        if (selectedProfileImage)
                          formData.append(
                            "profile_image",
                            selectedProfileImage,
                          );
                        const response = await fetch(
                          SummaryApi.updateUser.url,
                          {
                            method: SummaryApi.updateUser.method,
                            body: formData,
                          },
                        );
                        const result = await response.json();
                        if (result.status) {
                          showSuccessToast(
                            "Success!",
                            "Profile updated successfully!",
                          );

                          setUserDetails((prev) => ({
                            ...prev,
                            ...editFormData,
                            profile_image:
                              result.data?.profile_image || prev.profile_image,
                          }));
                          if (result.data?.profile_image) {
                            setProfilePreview(
                              `${backendDomain}/${result.data.profile_image}`,
                            );
                          }
                          setEditModalOpen(false);
                          fetchOrders();
                        } else {
                          toast.error(
                            result.message || "Failed to update profile",
                          );
                        }
                      } catch (error) {
                        toast.error("Error updating profile");
                      } finally {
                        setUpdating(false);
                      }
                    }}
                    disabled={updating}
                    className="px-6 py-2.5 text-sm bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg disabled:opacity-70 font-medium transition"
                  >
                    {updating ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PersonalDashboard;
